import { z } from 'zod';
import type { SseEventDef } from './sse-event-def';
import { buildSseChannelId } from './sse-event-def';

export const SseInnerEnvelopeSchema = z.object({
  id: z.string(),
  type: z.string(),
  data: z.unknown(),
  target: z
    .object({
      type: z.string(),
      id: z.string(),
    })
    .optional(),
});

export const SseEnvelopeSchema = z.object({
  type: z.literal('data'),
  data: SseInnerEnvelopeSchema,
});

export type SseEnvelope = z.infer<typeof SseEnvelopeSchema>;
export type SseInnerEnvelope = z.infer<typeof SseInnerEnvelopeSchema>;

export function buildSseEnvelope<T>(
  def: SseEventDef<T>,
  payload: T,
): SseEnvelope {
  const target = def.target
    ? {
        type:
          typeof def.target.type === 'function'
            ? def.target.type(payload)
            : def.target.type,
        id: def.target.id(payload),
      }
    : undefined;

  return {
    type: 'data',
    data: {
      id: buildSseChannelId(def, payload),
      type: def.type,
      data: payload,
      target,
    },
  };
}

export function matchSseEvent<T>(
  inner: SseInnerEnvelope,
  def: SseEventDef<T>,
): T | null {
  if (inner.type !== def.type) return null;
  if (!inner.id.startsWith(`${def.channelPrefix}:`)) return null;
  const parsed = def.payload.safeParse(inner.data);
  return parsed.success ? parsed.data : null;
}
